import { useEffect, useState } from 'react'
import { Bell, Facebook, Flame, Heart, MessageCircle, Moon, Music, Pause, Send, Sparkles, Sun, ZapOff } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useToast } from '../../lib/toast'

type Props = {
  isAuthenticated?: boolean
  onOpenAuth?: () => void
  onActionComplete?: () => void
}

export default function CustomerRightSideBarQuickActions({
  isAuthenticated = false,
  onOpenAuth,
  onActionComplete,
}: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const { unreadCount, toggleNotifications } = useToast()
  const [isDark, setIsDark] = useState(false)
  const [isMusicOn, setIsMusicOn] = useState(false)
  const [isEffectsOn, setIsEffectsOn] = useState(false)

  useEffect(() => {
    // Effects check
    const storedEffects = window.localStorage.getItem('digigo-effects')
    if (storedEffects === 'on') setIsEffectsOn(true)

    const onEffectsChanged = (ev: Event) => {
      const isOn = (ev as CustomEvent<{ isEffectsOn: boolean }>).detail?.isEffectsOn
      if (typeof isOn === 'boolean') setIsEffectsOn(isOn)
    }
    window.addEventListener('digigo-effects-changed', onEffectsChanged)
    return () => window.removeEventListener('digigo-effects-changed', onEffectsChanged)
  }, [])

  useEffect(() => {
    const stored = window.localStorage.getItem('digigo-theme')
    const shouldBeDark = stored
      ? stored === 'dark'
      : window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    setIsDark(shouldBeDark)

    const onThemeChanged = (ev: Event) => {
      const nextIsDark = (ev as CustomEvent<{ isDark: boolean }>).detail?.isDark
      if (typeof nextIsDark === 'boolean') setIsDark(nextIsDark)
    }
    window.addEventListener('digigo-theme-changed', onThemeChanged)
    return () => window.removeEventListener('digigo-theme-changed', onThemeChanged)
  }, [])

  useEffect(() => {
    const onMusicChanged = (ev: Event) => {
      const next = (ev as CustomEvent<{ isMusicOn: boolean }>).detail?.isMusicOn
      if (typeof next === 'boolean') setIsMusicOn(next)
    }
    window.addEventListener('digigo-music-changed', onMusicChanged)
    return () => window.removeEventListener('digigo-music-changed', onMusicChanged)
  }, [])

  const toggleTheme = () => {
    setIsDark((prev) => {
      const next = !prev
      window.localStorage.setItem('digigo-theme', next ? 'dark' : 'light')
      document.documentElement.classList.toggle('dark', next)
      window.dispatchEvent(new CustomEvent('digigo-theme-changed', { detail: { isDark: next } }))
      return next
    })
  }

  const toggleMusic = () => {
    window.dispatchEvent(new Event('digigo-music-toggle'))
  }

  const toggleEffects = () => {
    const next = !isEffectsOn
    window.localStorage.setItem('digigo-effects', next ? 'on' : 'off')
    window.dispatchEvent(new CustomEvent('digigo-effects-changed', { detail: { isEffectsOn: next } }))
    setIsEffectsOn(next)
  }

  const openFavorites = () => {
    if (!isAuthenticated) {
      onOpenAuth?.()
      onActionComplete?.()
      return
    }

    const params = new URLSearchParams(location.pathname === '/products' ? location.search : '')
    params.set('category', 'favorites')
    const search = params.toString()

    navigate({ pathname: '/products', search: search ? `?${search}` : '?category=favorites' })
    onActionComplete?.()
  }

  const openHotProducts = () => {
    if (location.pathname !== '/products') {
      const params = new URLSearchParams()
      params.set('focus', 'hot')
      navigate({ pathname: '/products', search: `?${params.toString()}` })
      onActionComplete?.()
      return
    }

    window.dispatchEvent(new Event('digigo-open-hot-product'))
    onActionComplete?.()
  }

  return (
    <div className="px-4 pb-4">
      <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-slate-50/50 dark:bg-white/5 p-3 space-y-3">
        <div className="text-xs font-black tracking-wide text-slate-900 dark:text-white/90">Nút nhanh</div>

        <div className="space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Nhóm tiện ích</div>

          <div className="grid grid-cols-4 gap-2">
            <button
              type="button"
              onClick={openFavorites}
              className="h-10 rounded-xl border border-rose-200/90 dark:border-rose-400/30 bg-rose-50/80 dark:bg-rose-500/10 hover:bg-rose-100 dark:hover:bg-rose-500/20 transition-colors grid place-items-center text-rose-600 dark:text-rose-300 shadow-sm dark:shadow-none"
              title={isAuthenticated ? 'Mở danh mục yêu thích' : 'Đăng nhập để xem yêu thích'}
              aria-label={isAuthenticated ? 'Mở danh mục yêu thích' : 'Đăng nhập để xem yêu thích'}
            >
              <Heart className="h-5 w-5" fill={isAuthenticated ? 'currentColor' : 'none'} />
            </button>

            <button
              type="button"
              onClick={openHotProducts}
              className="h-10 rounded-xl border border-red-200/90 dark:border-red-400/30 bg-red-50/80 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors grid place-items-center text-red-600 dark:text-red-300 shadow-sm dark:shadow-none"
              title="Mở sản phẩm hot"
              aria-label="Mở sản phẩm hot"
            >
              <Flame className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={toggleMusic}
              className="h-10 rounded-xl border border-amber-200/90 dark:border-amber-400/30 bg-amber-50/80 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors grid place-items-center text-amber-600 dark:text-amber-300 shadow-sm dark:shadow-none"
              title={isMusicOn ? 'Tắt nhạc nền' : 'Bật nhạc nền'}
              aria-label={isMusicOn ? 'Tắt nhạc nền' : 'Bật nhạc nền'}
            >
              {isMusicOn ? <Pause className="h-5 w-5" /> : <Music className="h-5 w-5" />}
            </button>

            <button
              type="button"
              onClick={toggleEffects}
              className="h-10 rounded-xl border border-violet-200/90 dark:border-violet-400/30 bg-violet-50/80 dark:bg-violet-500/10 hover:bg-violet-100 dark:hover:bg-violet-500/20 transition-colors grid place-items-center text-violet-600 dark:text-violet-300 shadow-sm dark:shadow-none"
              title={isEffectsOn ? 'Tắt hiệu ứng' : 'Bật hiệu ứng'}
              aria-label={isEffectsOn ? 'Tắt hiệu ứng' : 'Bật hiệu ứng'}
            >
              {isEffectsOn ? <Sparkles className="h-5 w-5 text-purple-500" /> : <ZapOff className="h-5 w-5" />}
            </button>

            <button
              type="button"
              onClick={toggleTheme}
              className="h-10 rounded-xl border border-blue-200/90 dark:border-blue-400/30 bg-blue-50/80 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors grid place-items-center text-blue-600 dark:text-blue-300 shadow-sm dark:shadow-none"
              title={isDark ? 'Chuyển sang chế độ sáng' : 'Chuyển sang chế độ tối'}
              aria-label={isDark ? 'Chuyển sang chế độ sáng' : 'Chuyển sang chế độ tối'}
            >
              {isDark ? <Moon className="h-5 w-5 text-indigo-300" /> : <Sun className="h-5 w-5 text-yellow-500" />}
            </button>

            <button
              type="button"
              onClick={toggleNotifications}
              className="h-10 rounded-xl border border-orange-200/90 dark:border-orange-400/30 bg-orange-50/80 dark:bg-orange-500/10 hover:bg-orange-100 dark:hover:bg-orange-500/20 transition-colors grid place-items-center text-orange-600 dark:text-orange-300 relative shadow-sm dark:shadow-none"
              title="Thông báo"
              aria-label="Thông báo"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 ? (
                <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] leading-5 text-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              ) : null}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Liên hệ</div>

          <div className="grid grid-cols-3 gap-2">
            <a
              href="https://t.me/+_KxbPXTAu7AzYjg1"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onActionComplete?.()}
              className="h-10 rounded-xl border border-sky-200/90 dark:border-sky-400/30 bg-sky-50/80 dark:bg-sky-500/10 hover:bg-sky-100 dark:hover:bg-sky-500/20 transition-colors grid place-items-center text-sky-600 dark:text-sky-300 shadow-sm dark:shadow-none"
              title="Telegram nhóm"
              aria-label="Telegram nhóm"
            >
              <Send className="h-5 w-5" />
            </a>

            <a
              href="https://zalo.me/0794174833"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onActionComplete?.()}
              className="h-10 rounded-xl border border-cyan-200/90 dark:border-cyan-400/30 bg-cyan-50/80 dark:bg-cyan-500/10 hover:bg-cyan-100 dark:hover:bg-cyan-500/20 transition-colors grid place-items-center text-cyan-600 dark:text-cyan-300 shadow-sm dark:shadow-none"
              title="Zalo liên hệ"
              aria-label="Zalo liên hệ"
            >
              <MessageCircle className="h-5 w-5" />
            </a>

            <a
              href="https://www.facebook.com/profile.php?id=61585243452584"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onActionComplete?.()}
              className="h-10 rounded-xl border border-indigo-200/90 dark:border-indigo-400/30 bg-indigo-50/80 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors grid place-items-center text-indigo-600 dark:text-indigo-300 shadow-sm dark:shadow-none"
              title="Facebook"
              aria-label="Facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
